import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Search, Bell, ShoppingCart, MessageSquare, Coins, Heart, ShoppingBag, Reply, Bot, PackageOpen, X, User, LayoutDashboard, Settings, LogOut, LogIn, Wallet } from 'lucide-react';
import NuyIcon from '../common/NuyIcon';
import ThemeToggle from '../common/ThemeToggle';
import { openDrawer } from './ChatDrawer';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber, timeAgo } from '../../lib/format';
import api from '../../lib/api';
import { createSocket } from '../../lib/ws';
import { usePublicSettings } from '../../hooks/useApi';

const NOTIF_ICON = { dm: MessageSquare, wallet: Coins, tip: Coins, like: Heart, sale: ShoppingBag, order: ShoppingBag, purchase: ShoppingBag, reply: Reply, comment: Reply, stock: PackageOpen };

function SearchBox({ query, setQuery, results, searchOpen, setSearchOpen, navigate, autoFocus, onPick }) {
  const submit = (e) => { e.preventDefault(); if (query.trim()) { navigate(`/marketplace?q=${encodeURIComponent(query.trim())}`); onPick?.(); } };
  const go = (to) => { setSearchOpen(false); setQuery(''); navigate(to); onPick?.(); };
  const empty = results && ['users', 'listings', 'resources', 'threads', 'blog'].every((k) => !results[k]?.length);
  return (
    <form onSubmit={submit} className="relative w-full">
      <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => results && setSearchOpen(true)} autoFocus={autoFocus}
        placeholder="Search users, forums, resources..." className="w-full bg-black/50 border border-white/[0.08] rounded-xl pl-10 pr-4 py-2 text-sm placeholder-gx-muted focus:outline-none focus:border-gx-cyan focus:ring-1 focus:ring-gx-cyan transition" data-testid="topbar-search" />
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gx-muted" />
      {searchOpen && results && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-black/90 backdrop-blur-2xl border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="search-dropdown">
          {empty && <p className="text-xs text-gx-muted p-4 text-center">No results for "{query}"</p>}
          {results.users?.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-display text-gx-muted">Users</p>
              {results.users.map((u) => (
                <button type="button" key={u.username} onClick={() => go(`/user/${u.username}`)} className="w-full flex items-center gap-2.5 px-4 py-2 text-left hover:bg-white/5 transition">
                  <img src={u.avatar_url} className="w-6 h-6 rounded-full" alt="" /><span className="text-sm font-display">{u.username}</span>
                </button>
              ))}
            </div>
          )}
          {[['listings', 'Marketplace', (i) => `/marketplace/${i.slug}`], ['resources', 'Resources', (i) => `/resources/${i.slug}`], ['threads', 'Forums', (i) => `/forums/thread/${i.slug}`], ['blog', 'Blog', (i) => `/blog/${i.slug}`]].map(([k, lbl, to]) => (
            results[k]?.length > 0 && (
              <div key={k}>
                <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-display text-gx-muted">{lbl}</p>
                {results[k].map((i) => (
                  <button type="button" key={i.slug} onClick={() => go(to(i))} className="w-full flex items-center justify-between gap-2 px-4 py-2 text-left hover:bg-white/5 transition">
                    <span className="text-sm truncate">{i.title}</span>
                    {i.price_usd != null && <span className="font-mono text-xs text-gx-cyan flex-shrink-0">${i.price_usd.toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            )
          ))}
        </div>
      )}
    </form>
  );
}

export default function Topbar({ onToggleMobile }) {
  const { isAuthed, user, logout, openAuth } = useAuth();
  const { data: site } = usePublicSettings();
  const cleanUrl = (u) => (u && /^(https?:\/\/|\/)[^\s,]+$/.test(String(u).trim()) ? String(u).trim() : null);
  const logoUrl = cleanUrl(site?.logo_url);
  const logoIconUrl = cleanUrl(site?.logo_icon_url) || cleanUrl(site?.favicon_url);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);
  const msearchRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      const inSearch = (searchRef.current && searchRef.current.contains(e.target)) || (msearchRef.current && msearchRef.current.contains(e.target));
      if (!inSearch) setSearchOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get('/search', { params: { q: query.trim() } }).then((r) => { setResults(r.data); setSearchOpen(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!isAuthed || !user?.id) { setNotifs([]); return; }
    api.get('/notifications').then((r) => setNotifs(r.data)).catch(() => {});
    const sock = createSocket(`/notifications/${user.id}`, {
      onMessage: (m) => { if (m.type === 'notification') setNotifs((prev) => [m.payload, ...prev].slice(0, 50)); },
    });
    return () => sock.close();
  }, [isAuthed, user?.id]);

  const unread = notifs.filter((n) => !n.read).length;
  const markAllRead = async () => {
    try { await api.post('/notifications/read'); } catch {}
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };
  const clickNotif = async (n) => {
    setNotifOpen(false);
    if (!n.read) {
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.link) navigate(n.link);
  };
  const searchProps = { query, setQuery, results, searchOpen, setSearchOpen, navigate };

  return (
    <header className="fixed top-0 right-0 md:left-64 left-0 z-30 h-16 bg-black/30 backdrop-blur-2xl border-b border-white/[0.05] gx-topbar transition-[left] duration-300" data-testid="topbar">
      <div className="flex items-center justify-between h-full px-3 sm:px-4 md:px-8 gap-3 max-w-[1600px] mx-auto w-full">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button className="md:hidden gx-top-icon !border-0 !bg-transparent" onClick={onToggleMobile} data-testid="topbar-mobile-menu" aria-label="Menu">
            <Menu className="!w-6 !h-6" />
          </button>
          <Link to="/" className="md:hidden flex items-center shrink-0 gx-topbar-mobile-logo" data-testid="topbar-mobile-logo">
            {logoUrl ? <img src={logoUrl} alt={site?.site_name || 'NUYED'} />
              : logoIconUrl ? <img src={logoIconUrl} className="!w-8 !h-8 rounded-lg" alt={site?.site_name || 'NUYED'} />
              : <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-gx-electric to-gx-cyan text-black font-display font-black text-lg">N</span>}
          </Link>
          <div className="relative max-w-md w-full hidden md:block" ref={searchRef}>
            <SearchBox {...searchProps} />
          </div>
        </div>

        <div className="gx-top-right">
          <button className="gx-top-icon gx-mobile-only" onClick={() => setMobileSearch((o) => !o)} aria-label="Search" data-testid="topbar-mobile-search">
            {mobileSearch ? <X /> : <Search />}
          </button>
          {isAuthed ? (
            <>
              <div className="relative" ref={notifRef}>
                <button className="gx-top-icon" onClick={() => setNotifOpen((o) => !o)} aria-label="Notifications" data-testid="topbar-notif-bell">
                  <Bell />
                  {unread > 0 && <span className="gx-top-badge" data-testid="notif-badge">{unread > 9 ? '9+' : unread}</span>}
                </button>
                {notifOpen && (
                  <div className="gx-menu !w-80 max-w-[calc(100vw-1.5rem)]" data-testid="notif-dropdown">
                    <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
                      <p className="font-display font-bold text-sm">Notifications</p>
                      {unread > 0 ? (
                        <button onClick={markAllRead} className="text-[10px] text-gx-cyan hover:underline font-display" data-testid="notif-mark-all-read">Mark all read</button>
                      ) : <span className="text-[10px] text-gx-muted font-mono">live</span>}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifs.length === 0 && <p className="text-xs text-gx-muted p-4 text-center">No notifications yet.</p>}
                      {notifs.slice(0, 15).map((n) => {
                        const Ic = NOTIF_ICON[n.type] || Bell;
                        return (
                          <button key={n.id} onClick={() => clickNotif(n)} className={`w-full flex items-start gap-3 px-4 py-3 text-left transition border-b border-white/[0.04] last:border-0 ${n.read ? 'hover:bg-white/5' : 'bg-gx-cyan/[0.05] hover:bg-gx-cyan/[0.09]'}`} data-testid={`notif-item-${n.id}`}>
                            <span className="w-8 h-8 rounded-lg bg-gx-cyan/10 text-gx-cyan grid place-items-center flex-shrink-0 mt-0.5"><Ic className="w-4 h-4" /></span>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs leading-snug">{n.text}</p>
                              <p className="text-[10px] text-gx-muted font-mono mt-0.5">{timeAgo(n.created_at)}</p>
                            </div>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-gx-cyan flex-shrink-0 mt-1.5 shadow-[0_0_8px_rgba(0,229,255,0.8)]" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => openDrawer('bot')} className="gx-top-icon is-bot" title="NuyBot AI" aria-label="NuyBot AI" data-testid="topbar-nuybot"><Bot /></button>
              <ThemeToggle />
              <Link to="/wallet" className="gx-top-pill gx-lg-only" data-testid="topbar-balance">
                <NuyIcon className="w-4 h-4" />
                <span className="font-mono text-sm">{formatNumber(user.gax_balance)}</span>
              </Link>
              <Link to="/cart" className="gx-top-icon gx-sm-only" aria-label="Cart" data-testid="topbar-cart-link"><ShoppingCart /></Link>
              <div className="relative" ref={menuRef}>
                <button className="flex items-center rounded-[11px]" onClick={() => setMenuOpen((o) => !o)} data-testid="topbar-user-menu" aria-label="Account menu">
                  <img src={user.avatar_url} className="gx-top-avatar" alt="avatar" />
                </button>
                {menuOpen && (
                  <div className="gx-menu" data-testid="user-menu">
                    <div className="gx-menu-head">
                      <img src={user.avatar_url} alt="" />
                      <div className="min-w-0">
                        <p className="font-display font-bold text-sm truncate">{user.username}{user.plus && <span className="gx-plus-badge">✦</span>}</p>
                        <p className="text-[11px] text-gx-muted truncate">{user.email}</p>
                      </div>
                    </div>
                    <Link to="/wallet" className="gx-menu-bal gx-below-lg" onClick={() => setMenuOpen(false)} data-testid="user-menu-balance">
                      <span className="flex items-center gap-1.5 text-xs font-display font-semibold text-gx-muted"><NuyIcon className="w-3.5 h-3.5" /> NUY balance</span>
                      <b>{formatNumber(user.gax_balance)}</b>
                    </Link>
                    <Link to={`/user/${user.username}`} className="gx-menu-item" onClick={() => setMenuOpen(false)} data-testid="user-menu-profile"><User /> My profile</Link>
                    <Link to="/dashboard" className="gx-menu-item" onClick={() => setMenuOpen(false)} data-testid="user-menu-dashboard"><LayoutDashboard /> Dashboard</Link>
                    <Link to="/messages" className="gx-menu-item" onClick={() => setMenuOpen(false)}><MessageSquare /> Messages</Link>
                    <Link to="/wallet" className="gx-menu-item" onClick={() => setMenuOpen(false)}><Wallet /> Wallet</Link>
                    <Link to="/cart" className="gx-menu-item gx-below-sm" onClick={() => setMenuOpen(false)}><ShoppingCart /> Cart</Link>
                    <Link to="/settings" className="gx-menu-item" onClick={() => setMenuOpen(false)} data-testid="user-menu-settings"><Settings /> Settings</Link>
                    <div className="gx-menu-sep" />
                    <button onClick={() => { logout(); setMenuOpen(false); navigate('/'); }} className="gx-menu-item is-danger" data-testid="user-menu-logout"><LogOut /> Log out</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => openDrawer('bot')} className="gx-top-icon is-bot gx-sm-only" title="NuyBot AI" aria-label="NuyBot AI" data-testid="topbar-nuybot"><Bot /></button>
              <ThemeToggle />
              <button onClick={() => openAuth('login')} className="gx-top-icon gx-xs-only" aria-label="Log in" data-testid="topbar-login-icon"><LogIn /></button>
              <button onClick={() => openAuth('login')} className="hidden sm:inline-flex px-3 py-2 rounded-full text-sm font-display font-medium text-gx-text hover:text-gx-cyan transition" data-testid="topbar-login">Log in</button>
              <button onClick={() => openAuth('register')} className="gx-btn-primary !rounded-full !py-2 !px-4" data-testid="topbar-register">Join</button>
            </>
          )}
        </div>
      </div>
      {mobileSearch && (
        <div className="gx-msearch md:hidden" ref={msearchRef} data-testid="topbar-mobile-search-bar">
          <SearchBox {...searchProps} autoFocus onPick={() => setMobileSearch(false)} />
        </div>
      )}
    </header>
  );
}
